import React, { FC, memo, useEffect, useId, useState } from "react";
import { Explainer, IconDenom, useTranslation } from "rujira.ui";
import { useRedactedV2 } from "../../redacted/v2/session";
import { V2ChainClient } from "../../redacted/v2/client";
import { deploymentConfig } from "../../redacted/v2/config";

// ============================================
// ICONS
// ============================================

const WalletIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...props}>
    <path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-1" />
    <path d="M18 12h.01" />
    <rect x="16" y="9" width="6" height="6" rx="1" />
  </svg>
));
WalletIcon.displayName = "WalletIcon";

const VaultIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="12" cy="12" r="3" />
    <path d="M12 9v1" />
    <path d="M12 14v1" />
    <path d="M9 12h1" />
    <path d="M14 12h1" />
  </svg>
));
VaultIcon.displayName = "VaultIcon";

// A payment card: what Spending is for.
const CardIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...props}>
    <rect x="2.5" y="5" width="19" height="14" rx="2" />
    <path d="M2.5 9.5h19" />
    <path d="M6 15h4" />
  </svg>
));
CardIcon.displayName = "CardIcon";

// Gas Tank Icon
export const GasTankIcon = memo((props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...props}>
    <path d="M4 20V6a2 2 0 012-2h8a2 2 0 012 2v14" />
    <path d="M4 20h12" />
    <path d="M6 10h8" />
    <path d="M16 10l2-2 2 2v6a2 2 0 01-2 2h0" />
    <path d="M18 16v-4" />
  </svg>
));
GasTankIcon.displayName = "GasTankIcon";

// ============================================
// PARTS
// ============================================

/** One place money can be: an icon, its name and one word about it. */
const Place: FC<{ icon: React.ReactNode; name: string; note: string }> = ({
  icon,
  name,
  note,
}) => (
  <div className="privacy-guide__place">
    <span className="privacy-guide__place-icon" aria-hidden="true">
      {icon}
    </span>
    <span className="fs-14 fw-500 color-white">{name}</span>
    <span className="fs-12 color-grey">{note}</span>
  </div>
);

/** The way between two places: both directions, or one label for both. */
const Way: FC<{ forth: string; back?: string }> = ({ forth, back }) => (
  <div className="privacy-guide__way fs-12 color-grey">
    <span>{forth}</span>
    <span className="privacy-guide__arrows" aria-hidden="true">
      ⇄
    </span>
    {back && <span>{back}</span>}
  </div>
);

/** The protocol fee, read from the current contract every time. */
const useProtocolFee = (proxyContract?: string) => {
  const [bps, setBps] = useState<number>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let current = true;
    setBps(undefined);
    setFailed(false);
    void (async () => {
      try {
        // Informational, read-only query. The transaction review separately
        // quotes and verifies exact charges immediately before confirmation.
        const config = await new V2ChainClient(
          deploymentConfig()
        ).contractConfig();
        const value = config.protocol_fee_bps;
        if (!Number.isSafeInteger(value) || value < 0 || value > 10_000)
          throw new Error("Invalid protocol fee");
        if (current) setBps(value);
      } catch {
        if (current) setFailed(true);
      }
    })();
    return () => {
      current = false;
    };
  }, [proxyContract]);
  return { bps, failed };
};

/** "0.15%" in the app's language (not the browser's). */
const percent = (value: number, language: string): string => {
  const options = { style: "percent", maximumFractionDigits: 2 } as const;
  try {
    return new Intl.NumberFormat(language, options).format(value);
  } catch {
    return new Intl.NumberFormat("en", options).format(value);
  }
};

const PrivacyFees: FC<{ proxyContract?: string }> = ({ proxyContract }) => {
  const { t, language } = useTranslation("portfolio");
  const { bps, failed } = useProtocolFee(proxyContract);
  const fee = bps === undefined ? undefined : percent(bps / 10_000, language);
  return (
    <>
      <div className="privacy-guide__fees">
        <span
          className="privacy-guide__fee fs-12"
          role={failed ? "alert" : "status"}>
          {fee !== undefined
            ? t("guideProtocolFee", { fee })
            : failed
              ? t("guideProtocolFeeUnavailable")
              : t("guideProtocolFeeLoading")}
        </span>
        <span className="privacy-guide__fee fs-12">{t("guideRelayerFee")}</span>
      </div>
      <p className="privacy-guide__text fs-12 color-grey">
        {t("guideFeesText")}
      </p>
    </>
  );
};

/** V1 only: swap fees are paid from a RUNE Gas Tank. */
const GasTank: FC = () => {
  const { t } = useTranslation("portfolio");
  return (
    <>
      <div className="privacy-guide__fees">
        <span className="privacy-guide__fee fs-12">
          <IconDenom denom="RUNE" className="privacy-guide__rune" />
          {t("guideGasTankRuneOnly")}
        </span>
        <span className="privacy-guide__fee fs-12">
          {t("guideGasTankSwapsOnly")}
        </span>
      </div>
      <p className="privacy-guide__text fs-12 color-grey">
        {t("guideGasTankText")}
      </p>
    </>
  );
};

// ============================================
// THE GUIDE
// ============================================

/**
 * "How Private Mode works" in the app's own modal: where funds are, the
 * three steps, a few tips (details in dotted explainers) and the fees. It
 * scrolls with the page like every app modal.
 */
export const PrivacyFlowModal: FC = () => {
  const { t } = useTranslation("portfolio");
  const v2 = useRedactedV2();
  const titleId = `privacy-guide-${useId().replace(/[^\w-]/g, "")}`;
  const steps: readonly { title: string; text: string }[] = [
    { title: t("guideStepDeposit"), text: t("guideStepDepositText") },
    { title: t("guideStepAct"), text: t("guideStepActText") },
    { title: t("guideStepWithdraw"), text: t("guideStepWithdrawText") },
  ];
  const tips: readonly { text: string; why: string }[] = [
    { text: t("guideTipSpendingPublic"), why: t("guideTipSpendingPublicWhy") },
    { text: t("privacyTipAmounts"), why: t("privacyTipAmountsWhy") },
    { text: t("privacyTipWaiting"), why: t("privacyTipWaitingWhy") },
    { text: t("privacyTipNewSpending"), why: t("privacyTipNewSpendingWhy") },
    { text: t("guideTipPrivacyColumn"), why: t("guideTipPrivacyColumnWhy") },
  ];
  return (
    <section className="privacy-guide" aria-labelledby={titleId}>
      <div className="modal__header">
        <h2 id={titleId}>{t("guideTitle")}</h2>
      </div>
      <p className="privacy-guide__text fs-14 color-grey">{t("guideIntro")}</p>

      <div className="privacy-guide__map">
        <Place
          icon={<WalletIcon />}
          name={t("guideWallet")}
          note={t("guideWalletNote")}
        />
        <Way forth={t("guideDeposit")} back={t("guideWithdraw")} />
        <Place
          icon={<VaultIcon />}
          name={t("guideReserve")}
          note={t("guideReserveNote")}
        />
        <Way forth={t("guideAutomatic")} />
        <Place
          icon={<CardIcon />}
          name={t("guideSpending")}
          note={t("guideSpendingNote")}
        />
      </div>

      <h3 className="privacy-guide__heading fs-14 fw-500 color-white">
        {t("guideStepsTitle")}
      </h3>
      <ol className="privacy-guide__steps">
        {steps.map(({ title, text }, index) => (
          <li key={title} className="privacy-guide__step">
            <span className="privacy-guide__number fs-12 fw-600">
              {index + 1}
            </span>
            <p className="privacy-guide__step-text fs-14 color-grey">
              <span className="fw-500 color-white">{title}</span> {text}
            </p>
          </li>
        ))}
      </ol>

      <h3 className="privacy-guide__heading fs-14 fw-500 color-white">
        {t("privacyTipsTitle")}
      </h3>
      <ul className="privacy-guide__tips fs-14 color-grey">
        {tips.map(({ text, why }) => (
          <li key={text}>
            <Explainer tip={why}>{text}</Explainer>
          </li>
        ))}
      </ul>

      <h3 className="privacy-guide__heading fs-14 fw-500 color-white">
        {v2.enabled ? t("guideFeesTitle") : t("guideGasTankTitle")}
      </h3>
      {v2.enabled ? (
        <PrivacyFees proxyContract={v2.proxyContract} />
      ) : (
        <GasTank />
      )}
    </section>
  );
};

export default PrivacyFlowModal;
